/* eslint-disable @typescript-eslint/no-explicit-any */
import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import loadAds from '@jobseeker/common-core/helpers/loadAds';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import jobFeedService from '@jobseeker/common-core/services/jobfeed.service';
import {AppState, wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';

import {Chat} from '@components';
import {metaInfo} from '@configs';
import {CATEGORY, JOBFEED} from '@constants';
import {loadSEO} from '@helpers';
import {JobFeed, NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {
  getConfigByApp,
  getIdBySlug,
  getLocationIds,
  isUserAgentForBot,
  removedUndefinedFromArrayObject,
  slugToTitle,
  utilServerSideDeviceDetection,
} from '@utils';
import {getListSEOContent, getPageUrl} from '@utils/seo';

import {
  COOKIE_LOGIN_AUTH,
  HIRIST_CATEGORY_BLOG_ID_MAPPING,
  HIRIST_CATEGORY_ID_MAPPING,
} from '@jobseeker/common-core/constants';
import cookie from 'cookie';
import {gladiatorService} from '@jobseeker/common-core/services';
import jobfeedService from '@jobseeker/common-core/services/jobfeed.service';
import {AdsElementType} from '@jobseeker/common-core/types';
import {getExpFilter, TrackEventUBA} from '@jobseeker/common-core/utils';
import type {GetServerSidePropsContext} from 'next/types';
import {useRouter} from 'next/router';
import {useEffect, useRef, useState} from 'react';

type CategoryFeedPageProps = {
  userIdSSR: number;
  slug: string;
  categoryTitle: string;
  categoryId: string;
  metaData: object;
  seoConfig: object;
  filters: any;
};
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const CategoryFeedPage = ({
  userIdSSR,
  categoryTitle,
  categoryId,
  metaData,
  filters,
  seoConfig,
  slug,
}: CategoryFeedPageProps) => {
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch);
  const router = useRouter();
  const {userId} = useSelector((state: AppState) => state.auth, shallowEqual);

  dispatch({
    type: 'AppContext/set_recap',
    payload: categoryId,
  });

  const breadcrumbElements = [
    {
      name: 'Home',
      item: '/',
    },
    {
      name: isAppIimjobs ? categoryTitle : `${categoryTitle} Jobs`,
      item: '',
    },
  ];

  const hasTracked = useRef(false);
  const initialPageTracked = useRef(false);
  const hasPageNoChanged = useRef(false);
  const hasFilterChanged = useRef(false);
  const [pageNumber, setPageNumber] = useState(filters?.page + 1);
  let sessionFilter: {minexp?: string; maxexp?: string} = {};
  try {
    sessionFilter = getExpFilter(CATEGORY);
  } catch (error) {}
  const [currentFilters, setCurrentFilters] = useState<{
    loc?: string[];
    minexp?: string;
    maxexp?: string;
    industry?: string[];
  }>({
    loc: filters.loc,
    minexp: (router.query.minexp as string) || (sessionFilter.minexp as string),
    maxexp: (router.query.maxexp as string) || (sessionFilter.maxexp as string),
    industry: filters.industry,
  });

  // Reset tracking when page number changes
  useEffect(() => {
    hasPageNoChanged.current = true;
  }, [pageNumber]);

  // Reset tracking when filter changes
  useEffect(() => {
    if (initialPageTracked.current) {
      hasFilterChanged.current = true;
    }
  }, [currentFilters]);

  useEffect(() => {
    if (
      hasTracked.current &&
      !hasPageNoChanged.current &&
      !hasFilterChanged.current
    )
      return;
    let actionType: string =
      pageNumber > filters.page + 1
        ? 'scroll'
        : initialPageTracked.current
        ? 'scroll'
        : 'view';
    if (hasFilterChanged.current) {
      actionType = 'filter';
    }

    const sessionUserId = userIdSSR || userId || '-1';

    hasTracked.current = true;
    hasPageNoChanged.current = false;
    hasFilterChanged.current = false;

    TrackEventUBA('categoryPageView', actionType, {
      ref: router.query.ref || '',
      filterExp: currentFilters?.maxexp
        ? `${currentFilters.minexp}-${currentFilters.maxexp}`
        : '',
      filterLocation: currentFilters?.loc?.join(',') || '',
      filterIndustry: currentFilters?.industry?.join(',') || '',
      pageName: 'Category Page',
      pageNo: pageNumber,
      categoryID: Number(categoryId) || '',
      categoryName: slugToTitle(slug),
      sessionUsername: `${sessionUserId}`,
    });
    initialPageTracked.current = true;
  }, [userId, pageNumber, currentFilters]);

  return (
    <>
      <Interceptor metaInfo={metaData}>
        <SeoLayout seoConfig={seoConfig} paginationEnabled={true} />
        <NavLayout showbottom={true}>
          <ResponsiveLayout
            variant={VersionVariants.VERSION_TWO}
            breadcrumbElements={breadcrumbElements}
          >
            <JobFeed
              jobFeedType={CATEGORY}
              catOrTagId={categoryId}
              catOrTagTitle={categoryTitle}
              showProfileCard={false}
              showFeatured={true}
              showLearning={true}
              preFilters={filters}
              setPageNumber={setPageNumber}
              onFiltersChange={setCurrentFilters}
            />
          </ResponsiveLayout>
        </NavLayout>
      </Interceptor>
      <Chat />
    </>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const pageNo = parseInt(context.query?.page) || 0;
    const minExp = parseInt(context.query?.minexp) || 0;
    const maxExp = parseInt(context.query?.maxexp) || 0;
    const posting = parseInt(context.query?.posting);
    const location = getLocationIds(context.query?.loc || '');
    const industry = context.query?.industry?.split(',') || [];
    const tabSlug = context.params?.slug || context.query?.slug;
    const slugUrl = context?.resolvedUrl || '';

    if (slugUrl == '/c/-jobs') {
      return {
        notFound: true,
      };
    }

    const redirectedFromExpiredJobDetailPage =
      process.env.NEXT_PUBLIC_APP_NAME === 'hirist' &&
      parseInt(String(context.query?.expJD), 10) === 1;

    const metaData = getConfigByApp(metaInfo, JOBFEED, true, CATEGORY);
    const {
      header: {
        meta: {tabsList},
      },
      ads: {
        featuredEmployer,
        diversity,
        learnGrow,
        featuredInstitutes,
        learningPartners,
        events,
        advertisementBanner,
      },
    } = metaData || {};

    const TEMP_CATEGORY_MAPPER: {[key: string]: string} = {
      finance: '/c/banking-finance-jobs',
      consulting: '/c/consulting-general-mgmt-jobs',
      hr: '/c/hr-ir-jobs',
      operations: '/c/scm-operations-jobs',
    };
    const {id, title, catId} = getIdBySlug(
      process.env.NEXT_PUBLIC_APP_NAME === 'hirist'
        ? 'categoryLocation'
        : 'category',
      tabSlug,
      tabsList,
      false,
      process.env.NEXT_PUBLIC_APP_NAME === 'hirist' ? true : false
    );

    let slugId = id;
    let slugTitle = title;

    const slugCatId = (catId ?? -1) > -1 ? catId : parseInt(slugId);

    if (!id && !title) {
      const slugFiltered = tabSlug?.replace(/-jobs$/, '');

      const newUrl = TEMP_CATEGORY_MAPPER[slugFiltered];
      if (newUrl) {
        return {
          redirect: {
            permanent: true,
            destination: newUrl,
          },
        };
      }
    }

    if (!slugId) {
      const tagInfo = await jobFeedService.tagIdBySlug(
        tabSlug?.replace(/-jobs$/, '')
      );

      slugId = tagInfo?.id;
      slugTitle = tagInfo?.title;
    }

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: CATEGORY},
    });

    if (parseInt(slugId) > -1) {
      store.dispatch({
        type: 'AppContext/set_navigation_selector',
        payload: slugCatId,
      });

      store.dispatch({
        type: 'AppContext/set_category_actual',
        payload: parseInt(slugId),
      });
    }

    if (typeof window === 'undefined') {
      store.dispatch({
        type: 'AppContext/set_enable_ads_preload',
        payload: true,
      });
    }

    store.dispatch({type: 'job/jobFeed/set_initial_state'});

    let userId = -1;

    try {
      if (context.req?.headers?.cookie) {
        const cookies = cookie.parse(context.req.headers.cookie);

        if (cookies?.[COOKIE_LOGIN_AUTH]) {
          const validateInfo = await gladiatorService.validate('validate', {
            headers: {
              cookie: context.req?.headers?.cookie,
            },
          });

          userId = parseInt(validateInfo?.userId ?? '0', 10);
        }
      }
    } catch (err) {}

    const locationIds = location
      ? location?.split(',')?.map(v => parseInt(v))
      : null;
    const filters = {
      page: pageNo > 0 ? pageNo - 1 : 0,
      minexp: minExp,
      maxexp: maxExp,
      loc: locationIds,
      industry,
      posting,
    };

    const isBotUser = isUserAgentForBot(context);
    let totalJobs = 0;
    if (!isAppIimjobs && isBotUser) {
      const CATEGORY_MAPPED =
        HIRIST_CATEGORY_BLOG_ID_MAPPING[
          HIRIST_CATEGORY_ID_MAPPING[slugId?.toString() || ''] ?? slugId
        ];
      const blogs = await jobfeedService.getLatestBlogs(CATEGORY_MAPPED ?? '');
      const bloData = (Object.values(blogs) as AdsElementType[])
        ?.map(blog => {
          return {
            link: blog?.link,
            title: blog?.title,
            imageUrl: blog?.featuredImage ?? '',
          };
        })
        ?.slice(0, 3);

      store.dispatch({
        type: 'Ads/set_blog_data',
        payload: bloData,
      });
    }

    if (isBotUser) {
      /**
       * Fetch the initial state for jobs list and related ads to show
       */
      const {
        auth: {userId},
      } = store.getState();

      store.dispatch({
        type: 'AppContext/set_bot',
        payload: true,
      });

      try {
        const jobFeeds: any = await jobFeedService.getCategoryJobs({
          userId,
          catId: slugId,
          pageNo: pageNo > 0 ? pageNo - 1 : 0,
          minexp: minExp,
          maxexp: maxExp,
          loc: location,
          size: 100,
        });

        totalJobs = jobFeeds?.totalJobs;
        store.dispatch({
          type: 'job/jobFeed/set_jobfeed',
          payload: jobFeeds,
        });
      } catch (error) {
        console.log(
          '🚀 ~ file: [slug].tsx:200 ~ store jobfeed category jobs=> ~ error:',
          error
        );
        return {
          notFound: true,
        };
      }

      try {
        if (featuredEmployer?.slot) {
          const ads = await loadAds(featuredEmployer.slot, {
            categoryIds: parseInt(slugId),
          });
          if (ads) {
            store.dispatch({
              type: 'Ads/set_featured_employer',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (diversity?.slot) {
          const ads = await loadAds(diversity.slot);
          if (ads) {
            store.dispatch({
              type: 'Ads/set_diversity',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }
        if (advertisementBanner?.slot) {
          const ads = await loadAds(advertisementBanner.slot, {
            categoryIds: parseInt(slugId),
            onRotation: 1,
          });
          if (ads) {
            store.dispatch({
              type: 'Ads/set_ad_banner',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (events?.slot) {
          const ads = await loadAds(events.slot);

          if (ads) {
            store.dispatch({
              type: 'Ads/set_events',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (learnGrow?.slot) {
          const ads = await loadAds(
            learnGrow.slot,
            {
              categoryIds: slugCatId,
              page: 0,
              size: 20,
              user_location: 0,
              shuffle: 1,
              orderby: 'modified',
            },
            true
          );
          if (ads) {
            store.dispatch({
              type: 'Ads/set_learn_grow',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (featuredInstitutes?.slot) {
          const ads = await loadAds(featuredInstitutes.slot, {
            categoryIds: slugCatId,
          });
          if (ads) {
            store.dispatch({
              type: 'Ads/set_featured_institutes',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }

        if (learningPartners?.slot) {
          const ads = await loadAds(learningPartners.slot);

          if (ads) {
            store.dispatch({
              type: 'Ads/set_learning_partner',
              payload: removedUndefinedFromArrayObject(ads),
            });
          }
        }
      } catch (error) {
        console.log(
          '🚀 ~ file: [slug].tsx:277 ~ store=> ~ category jobs error:',
          error
        );

        return {
          notFound: true,
        };
      }
    }

    const seoConfig = await loadSEO(
      CATEGORY,
      getListSEOContent(slugTitle || slugToTitle(tabSlug), pageNo, totalJobs),
      getPageUrl(slugUrl, pageNo),
      true,
      pageNo
    );

    const titleInfo = slugTitle || slugToTitle(tabSlug);

    store.dispatch({
      type: 'AppContext/set_slug_url',
      payload: tabSlug?.replace(/-jobs$/, ''),
    });
    store.dispatch({
      type: 'AppContext/set_slug',
      payload: titleInfo,
    });

    store.dispatch({
      type: 'AppContext/set_redirectedFromExpiredJD',
      payload: redirectedFromExpiredJobDetailPage,
    });

    return {
      props: {
        userIdSSR: userId,
        slug: tabSlug || '',
        categoryTitle: titleInfo,
        categoryId: slugId || '-1',
        metaData,
        seoConfig,
        filters,
      },
    };
  }
);

export default CategoryFeedPage;
